import { DatStep, StepStatus } from '@/components/CreateDatStepperModal';
import { NETWORK_CONFIG } from '@/constant';
import { getServerApi } from '@/gql';
import { getLazbubuDataConfig } from '@/utils';
import {
    trackAPICall,
    trackBlockchainOperation,
    trackDATStepEvent,
    trackError,
    trackFileOperation,
    trackUserAction,
} from '@/utils/gtag';
import * as Sentry from '@sentry/react';
import { encrypt } from 'alith/data';
import * as crypto from 'crypto';
import NodeRSA from 'node-rsa';
import { useCallback, useRef, useState } from 'react';
import { useAccount, useChainId, useWalletClient } from 'wagmi';
import {
    useDataRegistryContract,
    useVerifiedComputingContract,
} from './useContract';
import useSend from './useSend';

const ENCRYPTION_SEED = 'Sign to retrieve your encryption key';

// Define step ID constants
export const DAT_STEPS = {
    SIGN_AND_UPLOAD: 'sign-and-upload',
    ADD_FILE: 'add-file',
    ADD_PERMISSION: 'add-permission',
    REQUEST_PROOF: 'request-proof',
    REQUEST_REWARD: 'request-reward',
    SET_DAT_INFO: 'set-dat-info',
} as const;

// Custom error class for DAT creation steps
class DATStepError extends Error {
    public stepId: string;
    public originalError: any;

    constructor(stepId: string, originalError: any) {
        super(originalError.message || 'Transaction failed');
        this.stepId = stepId;
        this.originalError = originalError;
        this.name = 'DATStepError';
    }
}

// Get specific error message for each step
const getStepErrorMessage = (stepId: string, originalError?: any): string => {
    // 如果有原始错误信息，优先使用原始错误信息
    if (originalError?.message) {
        return `Transaction failed: ${originalError.message}`;
    }

    // 如果没有原始错误信息，使用预定义的错误消息
    switch (stepId) {
        case DAT_STEPS.ADD_FILE:
            return 'Transaction failed: Unable to register file to blockchain';
        case DAT_STEPS.ADD_PERMISSION:
            return 'Transaction failed: Unable to add file access permission';
        case DAT_STEPS.REQUEST_PROOF:
            return 'Transaction failed: Unable to request verification proof';
        case DAT_STEPS.REQUEST_REWARD:
            return 'Transaction failed: Unable to request reward';
        default:
            return 'Transaction failed';
    }
};

export default function useGenerateDat(
    lazbubuData: string,
    onCreateSuccess?: () => void,
) {
    const { data: walletClient } = useWalletClient();
    const { connector } = useAccount();
    const chainId = useChainId();
    const registryContract = useDataRegistryContract();
    const vcContract = useVerifiedComputingContract();
    const stepIdRef = useRef<string>();

    const send = useSend();

    // Get wallet name and format it for display
    const getWalletName = (connector: any): string => {
        if (!connector) return 'Unknown Wallet';
        const name = connector.name || 'Unknown';

        // Format common wallet names for better display
        switch (name.toLowerCase()) {
            case 'metamask':
                return 'MetaMask';
            case 'walletconnect':
                return 'WalletConnect';
            case 'coinbase wallet':
                return 'Coinbase Wallet';
            case 'injected':
                return 'Browser Wallet';
            default:
                return name;
        }
    };

    // Get chain name from chainId
    const getChainName = (chainId: number): string => {
        const network = NETWORK_CONFIG[chainId as keyof typeof NETWORK_CONFIG];
        return network ? network.name : `Chain ${chainId}`;
    };

    // Current wallet and chain info
    const currentWallet = getWalletName(connector);
    const currentChain = getChainName(chainId || 0);

    // Step management state
    const [steps, setSteps] = useState<DatStep[]>([
        {
            id: DAT_STEPS.SET_DAT_INFO,
            label: 'Set Lazbubu Information',
            description: 'Save Lazbubu name and avatar information',
            status: StepStatus.PENDING,
        },
        {
            id: DAT_STEPS.SIGN_AND_UPLOAD,
            label: 'Sign, Encrypt and Upload File',
            description:
                'Generate encryption key using wallet signature and upload data to server',
            status: StepStatus.PENDING,
        },
        {
            id: DAT_STEPS.ADD_FILE,
            label: 'Registering file on blockchain',
            description: 'Add file information to the data registry contract',
            status: StepStatus.PENDING,
        },

        {
            id: DAT_STEPS.REQUEST_PROOF,
            label: 'Request Verification Proof',
            description:
                'Request data proof from the verified computing contract',
            status: StepStatus.PENDING,
        },
        {
            id: DAT_STEPS.ADD_PERMISSION,
            label: 'Add File Access Permission',
            description:
                'Grant file access permission to the data registry contract',
            status: StepStatus.PENDING,
        },
        {
            id: DAT_STEPS.REQUEST_REWARD,
            label: 'Request Reward',
            description: 'Request reward from the data registry contract',
            status: StepStatus.PENDING,
        },
    ]);

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [isCreating, setIsCreating] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    // Helper function to send transaction with error handling
    const sendWithErrorHandling = useCallback(
        async (
            contract: any,
            method: string,
            params: any[],
            sendParams?: { value: string },
            stepId?: string,
        ) => {
            return new Promise((resolve, reject) => {
                send({
                    showPending: false,
                    contract,
                    method,
                    params,
                    sendParams,
                    showSuccess: false,
                    onSuccess: (result) => {
                        resolve(result);
                    },
                    onError: (error: any) => {
                        console.error(`${stepId || method} error:`, error);

                        // Send to Sentry
                        Sentry.captureException(error, {
                            tags: {
                                section: 'dat_creation',
                                step: stepId || 'unknown',
                                operation: 'blockchain_transaction',
                                contract_method: method,
                            },
                            extra: {
                                stepId: stepId || method,
                                contractMethod: method,
                                errorCode: error?.code,
                                params: params,
                                sendParams: sendParams,
                            },
                        });

                        // Track blockchain operation error
                        if (stepId) {
                            trackBlockchainOperation(
                                method,
                                'unknown',
                                'error',
                                undefined,
                                {
                                    error_code: error?.code,
                                    error_message: error?.message,
                                    step_id: stepId,
                                },
                            );
                        }

                        reject(new DATStepError(stepId || method, error));
                    },
                });
            });
        },
        [send],
    );

    // Helper function to update step status
    const updateStepStatus = useCallback(
        (stepId: string, status: StepStatus, error?: string) => {
            stepIdRef.current = stepId;
            setSteps((prevSteps) =>
                prevSteps.map((step) =>
                    step.id === stepId ? { ...step, status, error } : step,
                ),
            );
        },
        [],
    );

    // Move to next step
    const moveToNextStep = useCallback(() => {
        setCurrentStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
    }, [steps.length]);

    // Reset DAT creation process
    const resetDatCreation = useCallback(() => {
        setIsCreating(false);
        setIsCompleted(false);
        setCurrentStepIndex(0);
        setSteps((prevSteps) =>
            prevSteps.map((step) => ({
                ...step,
                status: StepStatus.PENDING,
                error: undefined,
            })),
        );
    }, []);

    const sinMessageToGetPassword = async () => {
        if (!walletClient) return;
        const signature = await walletClient.signMessage({
            message: ENCRYPTION_SEED,
        });
        return signature;
    };

    const calculateSHA256 = (text: string): string => {
        return crypto.createHash('sha256').update(text, 'utf8').digest('hex');
    };

    const proof = async (proofRequest: any) => {
        let base64ProofRequest;
        try {
            base64ProofRequest = Buffer.from(
                JSON.stringify(proofRequest),
            ).toString('base64');
            const response = await getServerApi().proof({
                proofRequest: base64ProofRequest,
            });

            console.log('proof response:', response.proof);
        } catch (error) {
            console.error('Proof API error:', error);

            // Send to Sentry
            Sentry.captureException(error, {
                tags: {
                    section: 'dat_creation',
                    step: DAT_STEPS.REQUEST_PROOF,
                    api: 'proof',
                },
                extra: {
                    stepId: DAT_STEPS.REQUEST_PROOF,
                    proofRequest: base64ProofRequest,
                },
            });

            // Track proof API error
            trackAPICall('proof', 'error', undefined, {
                error_message:
                    error instanceof Error ? error.message : String(error),
            });
            trackError(
                'proof_api_failed',
                error instanceof Error ? error.message : String(error),
                DAT_STEPS.REQUEST_PROOF,
            );

            // Create a custom error with step information
            const proofError = new Error(
                `Proof request failed: ${
                    error instanceof Error ? error.message : String(error)
                }`,
            );
            (proofError as any).stepId = DAT_STEPS.REQUEST_PROOF;
            throw proofError;
        }
    };

    const createLazbubu = async (formData: {
        lazbubuName: string;
        userName: string;
    }) => {
        try {
            // Track DAT creation start
            trackUserAction('start_dat_creation', 'dat_creation', {
                lazbubu_name: formData.lazbubuName,
                user_name: formData.userName,
            });

            setIsCreating(true);
            setIsCompleted(false);
            setCurrentStepIndex(0);

            // Reset all step statuses
            setSteps((prevSteps) =>
                prevSteps.map((step) => ({
                    ...step,
                    status: StepStatus.PENDING,
                    error: undefined,
                })),
            );

            updateStepStatus(DAT_STEPS.SET_DAT_INFO, StepStatus.LOADING);
            trackDATStepEvent(DAT_STEPS.SET_DAT_INFO, 'start');

            try {
                trackAPICall('setNames', 'start');
                const api = getServerApi();
                const apiStartTime = Date.now();
                const response = await api.setNames({
                    uuid: getLazbubuDataConfig(lazbubuData)?.uuid || '',
                    name: formData.lazbubuName,
                    masterName: formData.userName,
                });
                const apiResponseTime = Date.now() - apiStartTime;

                if (response.setNames) {
                    trackAPICall('setNames', 'success', apiResponseTime);
                    trackDATStepEvent(DAT_STEPS.SET_DAT_INFO, 'success');
                    updateStepStatus(
                        DAT_STEPS.SET_DAT_INFO,
                        StepStatus.COMPLETED,
                    );
                    moveToNextStep();
                } else {
                    trackAPICall('setNames', 'error', apiResponseTime, {
                        error_reason: 'API returned false',
                    });
                    trackDATStepEvent(DAT_STEPS.SET_DAT_INFO, 'error', {
                        error_reason: 'API returned false',
                    });
                    updateStepStatus(
                        DAT_STEPS.SET_DAT_INFO,
                        StepStatus.ERROR,
                        'Failed to set Lazbubu information',
                    );
                }
            } catch (error) {
                console.error('Set DAT info API error:', error);

                // Send to Sentry
                Sentry.captureException(error, {
                    tags: {
                        section: 'dat_creation',
                        step: DAT_STEPS.SET_DAT_INFO,
                        api: 'setNames',
                    },
                    extra: {
                        stepId: DAT_STEPS.SET_DAT_INFO,
                        lazbubuName: formData.lazbubuName,
                        userName: formData.userName,
                        uuid: getLazbubuDataConfig(lazbubuData)?.uuid || '',
                    },
                });

                trackAPICall('setNames', 'error', undefined, {
                    error_message:
                        error instanceof Error ? error.message : String(error),
                });
                trackError(
                    'api_error',
                    error instanceof Error ? error.message : String(error),
                    DAT_STEPS.SET_DAT_INFO,
                );
                // Create a custom error with step information
                const setDatError = new Error(
                    `Failed to set Lazbubu information: ${
                        error instanceof Error ? error.message : String(error)
                    }`,
                );
                (setDatError as any).stepId = DAT_STEPS.SET_DAT_INFO;
                throw setDatError;
            }

            updateStepStatus(DAT_STEPS.SIGN_AND_UPLOAD, StepStatus.LOADING);
            trackDATStepEvent(DAT_STEPS.SIGN_AND_UPLOAD, 'start');

            const password = await sinMessageToGetPassword();
            if (!password) {
                const signatureError = new Error(
                    'Failed to get wallet signature',
                );

                // Send to Sentry
                Sentry.captureException(signatureError, {
                    tags: {
                        section: 'dat_creation',
                        step: DAT_STEPS.SIGN_AND_UPLOAD,
                        operation: 'wallet_signature',
                    },
                    extra: {
                        stepId: DAT_STEPS.SIGN_AND_UPLOAD,
                        hasWalletClient: !!walletClient,
                        encryptionSeed: ENCRYPTION_SEED,
                    },
                });

                trackError(
                    'signature_failed',
                    'Failed to get wallet signature',
                    DAT_STEPS.SIGN_AND_UPLOAD,
                );
                trackDATStepEvent(DAT_STEPS.SIGN_AND_UPLOAD, 'error', {
                    error_reason: 'signature_failed',
                });
                updateStepStatus(
                    DAT_STEPS.SIGN_AND_UPLOAD,
                    StepStatus.ERROR,
                    'Failed to get signature',
                );
                return;
            }
            let url = '';
            try {
                trackFileOperation(
                    'encrypt',
                    'start',
                    Buffer.from(lazbubuData, 'utf8').length,
                );
                const encryptedData = await encrypt(
                    Buffer.from(lazbubuData, 'utf8'),
                    password,
                );
                trackFileOperation('encrypt', 'success', encryptedData.length);
                console.log(
                    'encryptedData ',
                    encryptedData,
                    encryptedData.length,
                );

                trackFileOperation('upload', 'start', encryptedData.length);
                trackAPICall('uploadFile', 'start');
                const base64Data =
                    Buffer.from(encryptedData).toString('base64');
                const uploadStartTime = Date.now();
                const response = await getServerApi().uploadFile({
                    data: base64Data,
                });
                const uploadResponseTime = Date.now() - uploadStartTime;

                console.log('uploadFile response:', response.uploadFile);
                url = response.uploadFile;

                trackFileOperation('upload', 'success', encryptedData.length);
                trackAPICall('uploadFile', 'success', uploadResponseTime);
            } catch (error) {
                console.error('Upload file API error:', error);

                // Send to Sentry
                Sentry.captureException(error, {
                    tags: {
                        section: 'dat_creation',
                        step: DAT_STEPS.SIGN_AND_UPLOAD,
                        api: 'uploadFile',
                        operation: 'file_upload',
                    },
                    extra: {
                        stepId: DAT_STEPS.SIGN_AND_UPLOAD,
                        fileSize: Buffer.from(lazbubuData, 'utf8').length,
                        password: password,
                    },
                });

                trackFileOperation('upload', 'error', undefined, {
                    error_message:
                        error instanceof Error ? error.message : String(error),
                });
                trackAPICall('uploadFile', 'error', undefined, {
                    error_message:
                        error instanceof Error ? error.message : String(error),
                });
                trackError(
                    'file_upload_failed',
                    error instanceof Error ? error.message : String(error),
                    DAT_STEPS.SIGN_AND_UPLOAD,
                );
                // Create a custom error with step information
                const uploadError = new Error(
                    `File upload failed: ${
                        error instanceof Error ? error.message : String(error)
                    }`,
                );
                (uploadError as any).stepId = DAT_STEPS.SIGN_AND_UPLOAD;
                throw uploadError;
            }
            if (!url) {
                const emptyUrlError = new Error(
                    'Upload API returned empty URL',
                );

                // Send to Sentry
                Sentry.captureException(emptyUrlError, {
                    tags: {
                        section: 'dat_creation',
                        step: DAT_STEPS.SIGN_AND_UPLOAD,
                        operation: 'file_upload_validation',
                    },
                    extra: {
                        stepId: DAT_STEPS.SIGN_AND_UPLOAD,
                        responseReceived: true,
                        urlEmpty: true,
                    },
                });

                trackError(
                    'upload_failed',
                    'Upload API returned empty URL',
                    DAT_STEPS.SIGN_AND_UPLOAD,
                );
                trackDATStepEvent(DAT_STEPS.SIGN_AND_UPLOAD, 'error', {
                    error_reason: 'empty_url',
                });
                updateStepStatus(
                    DAT_STEPS.SIGN_AND_UPLOAD,
                    StepStatus.ERROR,
                    'Failed to upload file',
                );
                return;
            }

            trackDATStepEvent(DAT_STEPS.SIGN_AND_UPLOAD, 'success');
            updateStepStatus(DAT_STEPS.SIGN_AND_UPLOAD, StepStatus.COMPLETED);
            moveToNextStep();
            if (
                !registryContract ||
                !vcContract ||
                !lazbubuData ||
                !url ||
                !password
            ) {
                console.log('registryContract', registryContract);
                console.log('vcContract', vcContract);
                console.log('lazbubuData', lazbubuData);
                console.log('url', url);
                console.log('password', password);

                const missingParamsError = new Error(
                    'Missing required parameters or contract instances',
                );

                // Send to Sentry
                Sentry.captureException(missingParamsError, {
                    tags: {
                        section: 'dat_creation',
                        step: 'validation',
                        operation: 'parameter_validation',
                    },
                    extra: {
                        hasRegistryContract: !!registryContract,
                        hasVcContract: !!vcContract,
                        lazbubuData: lazbubuData,
                        url: url,
                        password: password,
                        currentStep: DAT_STEPS.ADD_FILE,
                        walletType: connector?.name || 'Unknown',
                        chainId: chainId,
                    },
                });

                // Log wallet and chain information
                console.log('=== Wallet & Chain Info ===');
                console.log('Wallet:', connector?.name || 'Unknown');
                console.log('Chain ID:', chainId);
                console.log('========================');

                throw missingParamsError;
            }

            // Step 2: Register file to blockchain
            updateStepStatus(DAT_STEPS.ADD_FILE, StepStatus.LOADING);
            trackDATStepEvent(DAT_STEPS.ADD_FILE, 'start');

            let fileId = await registryContract.getFileIdByUrl(url);
            if (fileId == BigInt(0)) {
                const lazbubuDataSha256 = calculateSHA256(lazbubuData);

                trackBlockchainOperation('addFile', 'DataRegistry', 'start');
                await sendWithErrorHandling(
                    registryContract,
                    'addFile',
                    [url, lazbubuDataSha256],
                    undefined,
                    DAT_STEPS.ADD_FILE,
                );
                trackBlockchainOperation('addFile', 'DataRegistry', 'success');

                fileId = await registryContract.getFileIdByUrl(url);
            }

            trackDATStepEvent(DAT_STEPS.ADD_FILE, 'success', {
                file_id: fileId.toString(),
            });
            updateStepStatus(DAT_STEPS.ADD_FILE, StepStatus.COMPLETED);
            moveToNextStep();

            // Step 3: Add file access permission

            // Step 4: Request verification proof
            updateStepStatus(DAT_STEPS.REQUEST_PROOF, StepStatus.LOADING);
            trackDATStepEvent(DAT_STEPS.REQUEST_PROOF, 'start');

            trackBlockchainOperation(
                'requestProof',
                'VerifiedComputing',
                'start',
            );
            await sendWithErrorHandling(
                vcContract,
                'requestProof',
                [fileId],
                { value: '1' },
                DAT_STEPS.REQUEST_PROOF,
            );
            trackBlockchainOperation(
                'requestProof',
                'VerifiedComputing',
                'success',
            );

            trackDATStepEvent(DAT_STEPS.REQUEST_PROOF, 'success', {
                file_id: fileId.toString(),
            });
            updateStepStatus(DAT_STEPS.REQUEST_PROOF, StepStatus.COMPLETED);
            moveToNextStep();

            updateStepStatus(DAT_STEPS.ADD_PERMISSION, StepStatus.LOADING);
            trackDATStepEvent(DAT_STEPS.ADD_PERMISSION, 'start');

            const jobIds = await vcContract.fileJobIds(fileId);
            const jobId = jobIds[jobIds.length - 1];
            const job = await vcContract.getJob(jobId);
            if (!job) {
                const getJobError = new Error(`Failed to get job`);
                (getJobError as any).stepId = DAT_STEPS.ADD_PERMISSION;

                // Send to Sentry
                Sentry.captureException(getJobError, {
                    tags: {
                        section: 'dat_creation',
                        step: DAT_STEPS.ADD_PERMISSION,
                        operation: 'get_job',
                    },
                    extra: {
                        stepId: DAT_STEPS.ADD_PERMISSION,
                        jobId: jobId.toString(),
                        fileId: fileId.toString(),
                        jobIdsLength: jobIds.length,
                    },
                });

                trackError(
                    'job_not_found',
                    'Failed to get job from contract',
                    DAT_STEPS.ADD_PERMISSION,
                    { job_id: jobId.toString() },
                );
                trackDATStepEvent(DAT_STEPS.ADD_PERMISSION, 'error', {
                    error_reason: 'job_not_found',
                    job_id: jobId.toString(),
                });
                updateStepStatus(
                    DAT_STEPS.ADD_PERMISSION,
                    StepStatus.ERROR,
                    'Failed to get job',
                );
                console.error('Failed to get job');
                throw getJobError;
            }

            const nodeInfo = await vcContract.getNode(job.nodeAddress);
            const nodeUrl = nodeInfo.url;
            let pubKey = nodeInfo.publicKey;
            let rsa = new NodeRSA(pubKey, 'pkcs1-public-pem');
            let encryptedKey = rsa.encrypt(password, 'hex');
            console.log(`nodeUrl`, nodeUrl);
            console.log('jobId', jobId);
            console.log('fileId', fileId);
            console.log('url', url);
            console.log(`encryptedKey`, encryptedKey);

            trackBlockchainOperation(
                'addPermissionForFile',
                'DataRegistry',
                'start',
            );
            await sendWithErrorHandling(
                registryContract,
                'addPermissionForFile',
                [fileId, job.nodeAddress, encryptedKey],
                undefined,
                DAT_STEPS.ADD_PERMISSION,
            );
            trackBlockchainOperation(
                'addPermissionForFile',
                'DataRegistry',
                'success',
            );

            const proofRequest = {
                job_id: Number(jobId),
                file_id: Number(fileId),
                file_url: url,
                node_url: nodeUrl,
                encryption_key: encryptedKey,
                encryption_seed: ENCRYPTION_SEED,
                nonce: null,
                proof_url: null,
            };

            trackAPICall('proof', 'start');
            await proof(proofRequest);
            trackAPICall('proof', 'success');

            trackDATStepEvent(DAT_STEPS.ADD_PERMISSION, 'success', {
                job_id: jobId.toString(),
                file_id: fileId.toString(),
                node_address: job.nodeAddress,
            });
            updateStepStatus(DAT_STEPS.ADD_PERMISSION, StepStatus.COMPLETED);
            moveToNextStep();

            // Step 5: Request reward
            updateStepStatus(DAT_STEPS.REQUEST_REWARD, StepStatus.LOADING);
            trackDATStepEvent(DAT_STEPS.REQUEST_REWARD, 'start');

            trackBlockchainOperation('requestReward', 'DataRegistry', 'start');
            await sendWithErrorHandling(
                registryContract,
                'requestReward',
                [fileId, 1],
                undefined,
                DAT_STEPS.REQUEST_REWARD,
            );
            trackBlockchainOperation(
                'requestReward',
                'DataRegistry',
                'success',
            );

            trackDATStepEvent(DAT_STEPS.REQUEST_REWARD, 'success', {
                file_id: fileId.toString(),
                reward_amount: '1',
            });
            updateStepStatus(DAT_STEPS.REQUEST_REWARD, StepStatus.COMPLETED);
            moveToNextStep();

            setIsCompleted(true);

            // Track successful DAT creation completion
            trackUserAction('complete_dat_creation', 'dat_creation', {
                lazbubu_name: formData.lazbubuName,
                user_name: formData.userName,
                file_id: fileId.toString(),
            });

            if (onCreateSuccess) {
                onCreateSuccess();
            }

            console.log('Lazbubu created successfully!');
        } catch (error) {
            console.error('Failed to generate DAT:', error);

            if (error instanceof DATStepError) {
                // Handle DAT step specific errors
                const stepId = error.stepId;
                const originalError = error.originalError;

                console.log('DATStepError details:', {
                    stepId,
                    originalError,
                    errorMessage: originalError?.message,
                    errorCode: originalError?.code,
                });

                // Send DAT step error to Sentry
                Sentry.captureException(error, {
                    tags: {
                        section: 'dat_creation',
                        step: stepId,
                        error_type: 'dat_step_error',
                        error_code: originalError?.code || 'unknown',
                    },
                    extra: {
                        stepId,
                        originalErrorCode: originalError?.code,
                        originalErrorMessage: originalError?.message,
                        currentStepIndex,
                        totalSteps: steps.length,
                    },
                });

                if (originalError?.code === 'ACTION_REJECTED') {
                    // User cancelled transaction
                    trackError(
                        'transaction_cancelled',
                        'User cancelled the transaction',
                        stepId,
                        { error_code: originalError.code },
                    );
                    trackDATStepEvent(stepId, 'error', {
                        error_reason: 'user_cancelled',
                        error_code: originalError.code,
                    });
                    updateStepStatus(
                        stepId,
                        StepStatus.ERROR,
                        'User cancelled the transaction',
                    );
                } else if (originalError?.code === 'INSUFFICIENT_FUNDS') {
                    // Insufficient funds
                    trackError(
                        'insufficient_funds',
                        'Insufficient funds for transaction',
                        stepId,
                        { error_code: originalError.code },
                    );
                    trackDATStepEvent(stepId, 'error', {
                        error_reason: 'insufficient_funds',
                        error_code: originalError.code,
                    });
                    updateStepStatus(
                        stepId,
                        StepStatus.ERROR,
                        'Insufficient funds for transaction',
                    );
                } else if (originalError?.code === 'UNPREDICTABLE_GAS_LIMIT') {
                    // Gas limit error
                    trackError(
                        'gas_limit_exceeded',
                        'Transaction failed: Gas limit exceeded or transaction would fail',
                        stepId,
                        { error_code: originalError.code },
                    );
                    trackDATStepEvent(stepId, 'error', {
                        error_reason: 'gas_limit_exceeded',
                        error_code: originalError.code,
                    });
                    updateStepStatus(
                        stepId,
                        StepStatus.ERROR,
                        'Transaction failed: Gas limit exceeded or transaction would fail',
                    );
                } else {
                    // Transaction failed with other error
                    const errorMessage = getStepErrorMessage(
                        stepId,
                        originalError,
                    );
                    trackError('transaction_failed', errorMessage, stepId, {
                        error_code: originalError?.code,
                        original_message: originalError?.message,
                    });
                    trackDATStepEvent(stepId, 'error', {
                        error_reason: 'transaction_failed',
                        error_code: originalError?.code,
                        error_message: originalError?.message,
                    });
                    updateStepStatus(stepId, StepStatus.ERROR, errorMessage);
                }
            } else {
                // Handle other errors (like API errors)

                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : 'Unknown error occurred';

                // Try to find the current loading step
                let currentStep = steps.find(
                    (step) => step.id === stepIdRef.current,
                );

                // If no loading step found, try to determine which step failed based on error context
                if (!currentStep) {
                    // Check if error has stepId property (from our custom errors)
                    if (
                        error &&
                        typeof error === 'object' &&
                        'stepId' in error
                    ) {
                        const stepId = (error as any).stepId;
                        currentStep = steps.find((step) => step.id === stepId);
                    } else {
                        // Check if it's a GraphQL error (likely from proof or setNames)
                        const errorMessage =
                            error instanceof Error
                                ? error.message
                                : String(error);
                        if (
                            errorMessage &&
                            (errorMessage.includes('GraphQL') ||
                                errorMessage.includes('proof') ||
                                errorMessage.includes('setNames'))
                        ) {
                            // If it's a proof error, it's likely in REQUEST_PROOF step
                            if (errorMessage.includes('proof')) {
                                currentStep = steps.find(
                                    (step) =>
                                        step.id === DAT_STEPS.REQUEST_PROOF,
                                );
                            } else if (errorMessage.includes('setNames')) {
                                currentStep = steps.find(
                                    (step) =>
                                        step.id === DAT_STEPS.SET_DAT_INFO,
                                );
                            }
                        }

                        // If still no step found, use the current step index
                        if (!currentStep) {
                            currentStep = steps[currentStepIndex];
                        }
                    }
                }

                // Send general error to Sentry
                Sentry.captureException(error, {
                    tags: {
                        section: 'dat_creation',
                        step: currentStep?.id || 'unknown',
                        error_type: 'general_error',
                    },
                    extra: {
                        stepId: currentStep?.id || 'unknown',
                        currentStepIndex,
                        totalSteps: steps.length,
                        errorType:
                            error instanceof Error
                                ? error.constructor.name
                                : 'unknown',
                        hasStepIdProperty:
                            error &&
                            typeof error === 'object' &&
                            'stepId' in error,
                        isGraphQLError: errorMessage.includes('GraphQL'),
                        isProofError: errorMessage.includes('proof'),
                        isSetNamesError: errorMessage.includes('setNames'),
                    },
                });

                // Track the error
                if (currentStep) {
                    trackError('general_error', errorMessage, currentStep.id, {
                        step_index: currentStepIndex,
                        error_type:
                            error instanceof Error
                                ? error.constructor.name
                                : 'unknown',
                    });
                    trackDATStepEvent(currentStep.id, 'error', {
                        error_reason: 'general_error',
                        error_message: errorMessage,
                    });
                    updateStepStatus(
                        currentStep.id,
                        StepStatus.ERROR,
                        errorMessage,
                    );
                } else {
                    // Fallback: update the first step with error
                    const firstStep = steps[0];
                    if (firstStep) {
                        trackError(
                            'general_error',
                            errorMessage,
                            firstStep.id,
                            {
                                step_index: currentStepIndex,
                                error_type:
                                    error instanceof Error
                                        ? error.constructor.name
                                        : 'unknown',
                                fallback_step: true,
                            },
                        );
                        trackDATStepEvent(firstStep.id, 'error', {
                            error_reason: 'general_error_fallback',
                            error_message: errorMessage,
                        });
                        updateStepStatus(
                            firstStep.id,
                            StepStatus.ERROR,
                            errorMessage,
                        );
                    }
                }

                // Track overall DAT creation failure
                trackUserAction('fail_dat_creation', 'dat_creation', {
                    error_message: errorMessage,
                    final_step: currentStep?.id || 'unknown',
                });
            }
        } finally {
            // setIsCreating(false);
        }
    };

    return {
        createLazbubu,

        steps,
        currentStepIndex,
        isCreating,
        isCompleted,
        resetDatCreation,
    };
}
